import { BaseService } from './core/BaseService';
import { apiClient } from './core/ApiClient';

class BranchService extends BaseService {
  constructor() {
    super('/branches');
  }

  // Get all branches with enhanced filtering
  async getBranches(params = {}) {
    return this.getAll(params);
  }

  // Get single branch
  async getBranch(id) {
    return this.getById(id);
  }

  // Create branch
  async createBranch(branchData) {
    this.validateBranchData(branchData);
    return this.create(branchData);
  }

  // Update branch
  async updateBranch(id, branchData) {
    return this.update(id, branchData);
  }

  // Delete branch
  async deleteBranch(id) {
    return this.delete(id);
  }

  // Get branch users
  async getBranchUsers(branchId, params = {}) {
    return this.getRelated(branchId, 'users', params);
  }

  // Get branch tickets
  async getBranchTickets(branchId, params = {}) {
    return this.getRelated(branchId, 'tickets', params);
  }

  // Get branch statistics
  async getBranchStats(branchId) {
    return this.getRelated(branchId, 'stats');
  }

  // Get branch analytics
  async getBranchAnalytics(params = {}) {
    return this.customEndpoint('analytics/status-wise', 'GET', null, params);
  }

  // Search branches
  async searchBranches(query, filters = {}) {
    return this.search(query, filters);
  }

  // Activate/Deactivate branch
  async activateBranch(branchId) {
    return this.action(branchId, 'activate');
  }

  async deactivateBranch(branchId) {
    return this.action(branchId, 'deactivate');
  }

  // Bulk operations
  async bulkUpdateBranches(branchIds, updateData) {
    return this.bulkUpdate(branchIds, updateData);
  }

  async bulkDeleteBranches(branchIds) {
    return this.bulkDelete(branchIds);
  }

  // Export branches
  async exportBranches(params = {}, format = 'csv') {
    return this.export(params, format);
  }

  // Validation
  validateBranchData(data) {
    const required = ['name', 'code', 'city', 'state', 'country'];
    const missing = required.filter(field => !data[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate email format if provided
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      throw new Error('Invalid email format');
    }
    
    // Validate phone format if provided
    if (data.phone && !/^\+?[1-9]\d{1,14}$/.test(data.phone.replace(/[\s-]/g, ''))) {
      throw new Error('Invalid phone number format');
    }
  }

  // Check branch code availability
  async checkCodeAvailability(code, excludeId = null) {
    if (!code) throw new Error('Branch code is required');
    
    return this.customEndpoint('check-code', 'POST', { code, exclude_id: excludeId });
  }
}

// Export singleton instance
export const branchService = new BranchService();

// Export class for testing or custom instances
export { BranchService };